export type AWXBalance = {
  // Available balance. Customer deposits will be added to the available balance once processed, including any settled conversions bought in this currency
  available_amount: number
  // Currency of the account balance
  currency: string
  // Amount that will become available once currency conversion(s) have been settled
  pending_amount: number
  // Prepayment balance. Amount of money that is held in your Airwallex account as collateral for future transfer/conversion
  prepayment_amount: number
  // Reserved balance. Amount of money that is held in your Airwallex account
  reserved_amount: number
  // Total account balance (Available + Pending)
  total_amount: number
}

export type AWXBalanceHistory = {
  has_more: boolean
  items: {
    // The amount of this balance change
    amount: number
    // The balance after this balance change occurred
    balance: number
    // The currency of this balance change
    currency: string
    // The description for this balance change
    description: string
    // The fee amount occurred with same currency, the account who paid this fee will receive this field
    fee: number
    id: string
    // The time this change posted to balance
    posted_at: string
    // The source of this balance change
    source: string
    // The original transaction type.
    source_type: AWXBalanceItemSourceType
    // The transaction type of the balance history
    transaction_type: AWXBalanceItemTransactionType
  }[]
  page_after: string
  page_before: string
}

export enum AWXBalanceItemSourceType {
  DEPOSIT = 'DEPOSIT',
  CONVERSION = 'CONVERSION',
  PAYMENT = 'PAYMENT',
  CHARGE = 'CHARGE',
  TRANSFER = 'TRANSFER',
  ADJUSTMENT = 'ADJUSTMENT',
  FEE = 'FEE',
  ISSUING = 'ISSUING',
  PAYMENT_SETTLEMENT = 'PAYMENT_SETTLEMENT',
  PAYMENT_RESERVE = 'PAYMENT_RESERVE',
  PAYMENT_REFUND = 'PAYMENT_REFUND',
  PAYMENT_REFUND_FAILED = 'PAYMENT_REFUND_FAILED',
  DIRECT_DEBIT = 'DIRECT_DEBIT',
  PAYOUT = 'PAYOUT',
  PURCHASE = 'PURCHASE',
  BATCH_PAYOUT = 'BATCH_PAYOUT',
  YIELD = 'YIELD',
}

export enum AWXBalanceItemTransactionType {
  PAYMENT = 'PAYMENT',
  PRE_CHARGEBACK_ACCEPTED = 'PRE_CHARGEBACK_ACCEPTED',
  REFUND_CHARGEBACK_RECEIVED = 'REFUND_CHARGEBACK_RECEIVED',
  REFUND_CHARGEBACK_WITHDRAWN = 'REFUND_CHARGEBACK_WITHDRAWN',
  SECOND_CAPTURE_WITHDRAWN = 'SECOND_CAPTURE_WITHDRAWN',
  DISPUTE = 'DISPUTE',
  SECOND_DISPUTE = 'SECOND_DISPUTE',
  DISPUTE_REVERSAL = 'DISPUTE_REVERSAL',
  DISPUTE_LOST = 'DISPUTE_LOST',
  DISPUTE_WON = 'DISPUTE_WON',
  REFUND = 'REFUND',
  REFUND_REVERSAL = 'REFUND_REVERSAL',
  REFUND_FAILURE = 'REFUND_FAILURE',
  PAYMENT_RESERVE_HOLD = 'PAYMENT_RESERVE_HOLD',
  PAYMENT_RESERVE_RELEASE = 'PAYMENT_RESERVE_RELEASE',
  PAYOUT = 'PAYOUT',
  PAYOUT_FAILURE = 'PAYOUT_FAILURE',
  PAYOUT_REVERSAL = 'PAYOUT_REVERSAL',
  CONVERSION_SELL = 'CONVERSION_SELL',
  CONVERSION_BUY = 'CONVERSION_BUY',
  CONVERSION_REVERSAL = 'CONVERSION_REVERSAL',
  DEPOSIT = 'DEPOSIT',
  DEPOSIT_REVERSAL = 'DEPOSIT_REVERSAL',
  ADJUSTMENT = 'ADJUSTMENT',
  FEE = 'FEE',
  DD_CREDIT = 'DD_CREDIT',
  DD_DEBIT = 'DD_DEBIT',
  DC_CREDIT = 'DC_CREDIT',
  DC_DEBIT = 'DC_DEBIT',
  DC_DEBIT_REVERSAL = 'DC_DEBIT_REVERSAL',
  ISSUING_AUTHORISATION_HOLD = 'ISSUING_AUTHORISATION_HOLD',
  ISSUING_AUTHORISATION_RELEASE = 'ISSUING_AUTHORISATION_RELEASE',
  ISSUING_CAPTURE = 'ISSUING_CAPTURE',
  ISSUING_REFUND = 'ISSUING_REFUND',
  SPLIT = 'SPLIT',
  SPLIT_REVERSAL = 'SPLIT_REVERSAL',
  LENDER_FUNDS_BORROWED = 'LENDER_FUNDS_BORROWED',
  LENDER_FUNDS_RETURNED = 'LENDER_FUNDS_RETURNED',
  DIRECT_DEBIT = 'DIRECT_DEBIT',
  DIRECT_DEBIT_REVERSAL = 'DIRECT_DEBIT_REVERSAL',
  HOLD = 'HOLD',
  HOLD_RELEASE = 'HOLD_RELEASE',
  RETURN_TO_FUNDING_SOURCE = 'RETURN_TO_FUNDING_SOURCE',
  PURCHASE = 'PURCHASE',
  PURCHASE_REVERSAL = 'PURCHASE_REVERSAL',
  TRANSFER = 'TRANSFER',
  PREPAYMENT = 'PREPAYMENT',
  PREPAYMENT_RELEASE = 'PREPAYMENT_RELEASE',
}

export type AWXBalanceHistoryRequest = {
  // The transaction currency in 3-letter ISO-4217 format
  currency?: string
  // The start date (inclusive) of the date range during which balance activity occurred in your Wallet. 
  // Specify the date in ISO8601 format. If not specified, from_post_at defaults to 7 days before to_post_at
  from_post_at?: string
  to_post_at?: string
  // A bookmark for use in pagination to retrieve either the next page or the previous page of results. 
  // You can fetch the value for this identifier from the response of the previous API call. 
  // To retrieve the next page of results, pass the value of page_after (if not null) from the response to a subsequent call. 
  // To retrieve the previous page of results, pass the value of page_before (if not null) from the response to a subsequent call. 
  // Passing page=0 will override the 7 day default for null date ranges, enabling a complete search of the Wallet balance records
  page?: string
  // The page number of the page to be retrieved. Page numbers range from 0 to a maximum value of 10000
  page_num?: number
  // A limit on the number of records to be returned on a page, between 10 and 2000. The default is 100
  page_size?: number
  // request_id from clients for the transaction
  request_id?: string
}

export enum FinancialTransactionStatus {
  PENDING = 'PENDING',
  SETTLED = 'SETTLED',
  CANCELLED = 'CANCELLED',
}

// see https://www.airwallex.com/docs/api#/Finance/Financial_Transactions/_api_v1_financial_transactions__id_/get
export type FinancialTransaction = {
  // the gross amount of the transaction
  amount: number
  // The batch ID of the settlement that the transaction belongs to
  batch_id?: string
  // The client rate for the transaction
  client_rate?: string
  // The time this transaction was created at
  created_at: string
  // Currency the transaction should be made in (3-letter ISO-4217 code)
  currency: string
  // The currency pair that the client_rate is quoted in
  currency_pair?: string
  // The description of the transaction
  description?: string
  // The time when the transaction will be settled at
  estimated_settled_at?: string
  // Fee paid for the transaction
  fee?: number
  // The ID of the funding source from which the transaction originated
  funding_source_id: string
  // The ID of the transaction
  id: string
  // Net amount of the transaction
  net: number
  // The time when the transaction was settled at
  settled_at?: string
  // The source ID of the transaction
  source_id?: string
  // The type of the source transaction, One of
  //  PAYOUT, 
  //  CONVERSION, 
  //  DEPOSIT, 
  //  ADJUSTMENT, 
  //  FEE, 
  //  PAYMENT_ATTEMPT, 
  //  REFUND, 
  //  DISPUTE, 
  //  CHARGE, 
  //  TRANSFER, 
  //  YIELD, 
  //  BATCH_PAYOUT, 
  //  CARD_PURCHASE, 
  //  CARD_REFUND, 
  //  PURCHASE, 
  //  REFUND_REVERSAL
  source_type: string
  status: FinancialTransactionStatus
  transaction_type: AWXBalanceItemTransactionType
}

export enum FinancialTransactionSourceType {
  PAYOUT = 'PAYOUT',
  CONVERSION = 'CONVERSION',
  DEPOSIT = 'DEPOSIT',
  ADJUSTMENT = 'ADJUSTMENT',
  FEE = 'FEE',
  PAYMENT_ATTEMPT = 'PAYMENT_ATTEMPT',
  REFUND = 'REFUND',
  DISPUTE = 'DISPUTE',
  CHARGE = 'CHARGE',
  TRANSFER = 'TRANSFER',
  YIELD = 'YIELD',
  BATCH_PAYOUT = 'BATCH_PAYOUT',
  CARD_PURCHASE = 'CARD_PURCHASE',
  CARD_REFUND = 'CARD_REFUND',
  PURCHASE = 'PURCHASE',
  REFUND_REVERSAL = 'REFUND_REVERSAL',
}

export type FinancialTransactionItem = {
  amount: number
  batch_id?: string
  client_rate?: string
  created_at: string
  currency: string
  currency_pair?: string
  description?: string
  estimated_settled_at?: string
  fee?: number
  funding_source_id?: string
  id: string
  net: number
  settled_at?: string
  source_id?: string
  source_type: FinancialTransactionSourceType
  status: FinancialTransactionStatus
  transaction_type: AWXBalanceItemTransactionType
}


export type FinancialTransactionListResponse = {
  has_more: boolean
  items: FinancialTransactionItem[]
}

export type ListFinancialTransactionsRequest = {
  currency?: string
  page_num?: number
  page_size?: number
  from_created_at?: string
  to_created_at?: string
  source_id?: string
  connected_account_id: string
}

export type ListFinancialTransactionsBySourceIdRequest = {
  currency?: string
  page_num?: number
  page_size?: number
  source_id: string
  connected_account_id: string
}